import { useAtomValue } from "@effect/atom-react";
import { OrgSegmentCanonicalizer } from "./OrgSegmentCanonicalizer";
import {
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  type CSSProperties,
  type ReactNode,
} from "react";
import { useLocation, useNavigate } from "@tanstack/react-router";

import { isElectron } from "../env";
import { useAppSidebarWidth } from "../hooks/useAppSidebarWidth";
import { usePendingThreadSpaces } from "../hooks/usePendingThreadSpaces";
import { useToolBackShortcut } from "../hooks/useToolBackShortcut";
import { resolveShortcutCommand, shortcutLabelForCommand } from "../keybindings";
import { cn, isMacPlatform } from "../lib/utils";
import { primaryServerKeybindingsAtom } from "../state/server";
import { useEnvironmentIdentificationMode } from "../hooks/useSettings";
import { useAppAreaView } from "./apps/appAreaGates";
import { appsSidebarShown } from "./apps/appGate";
import { AppsSidebar } from "./apps/AppsSidebar";
import { appAreaFromPathname } from "./apps/appsPaths";
import { HomeSidebar } from "./home/HomeSidebar";
import { foldsIntoHomeSidebar } from "./home/HomeSidebar.logic";
import { isWorkflowsPathname } from "./home/homePaths";
import { isInboxPathname } from "./inbox/inboxPaths";
import { appSidebarSection } from "./appSidebarWidth";
import { RegulationSidebarFooter, RegulationSidebarNav } from "./regulation/RegulationSidebarNav";
import { useRegulationOrg } from "./regulation/useRegulationOrg";
import { NAV_RAIL_WIDTH, NavRail } from "./NavRail";
import { ShellTopBar } from "./ShellTopBar";
import { useSidebarPresenceSwitch } from "./shellSidebarPresence";
import { AppTour } from "./onboarding/AppTour";
import ThreadSidebar from "./Sidebar";
import { SettingsSidebarNav } from "./settings/SettingsSidebarNav";
import { SidebarContentSlot } from "./sidebar/SidebarContentSlot";
import {
  INBOX_SIDEBAR_MIN_WIDTH,
  INBOX_SIDEBAR_MAX_WIDTH,
  resolveInboxSidebarWidth,
} from "./inbox/inboxSidebarWidth";
import { SidebarChromeHeader } from "./sidebar/SidebarChrome";
import { useSidebarStageBackdropVariant } from "./SidebarStageBackdrop";
import { resolveThreadSidebarMaximumWidth, THREAD_SIDEBAR_MIN_WIDTH } from "./threadSidebarWidth";
import {
  Sidebar,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
  useSidebar,
  useSidebarVisibility,
} from "./ui/sidebar";
import { Tooltip, TooltipPopup, TooltipTrigger } from "./ui/tooltip";

// The floating toggle clears the native traffic lights at 90px from the
// window edge; the nav rail already occupies part of that distance.
const MACOS_TRAFFIC_LIGHTS_LEFT_INSET = "calc(90px - var(--nav-rail-width, 0px))";

function subscribeToViewportWidth(onChange: () => void): () => void {
  window.addEventListener("resize", onChange);
  return () => window.removeEventListener("resize", onChange);
}

function readViewportWidth(): number {
  return window.innerWidth;
}

export function SidebarControl() {
  const keybindings = useAtomValue(primaryServerKeybindingsAtom);
  const { isMobile, toggleSidebar } = useSidebar();
  const isSidebarVisible = useSidebarVisibility();
  const environmentIdentificationMode = useEnvironmentIdentificationMode();
  const stageBackdropVariant = useSidebarStageBackdropVariant(
    environmentIdentificationMode === "artwork",
  );
  const shortcutLabel = shortcutLabelForCommand(keybindings, "sidebar.toggle");

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;
      if (
        event.target instanceof HTMLElement &&
        event.target.closest("[data-keybinding-capture]")
      ) {
        return;
      }
      if (resolveShortcutCommand(event, keybindings) !== "sidebar.toggle") return;

      event.preventDefault();
      event.stopPropagation();
      toggleSidebar();
    };

    // Capture before focused editors consume commands such as Mod+B for rich-text formatting.
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [keybindings, toggleSidebar]);

  // The nav rail owns the toggle on desktop; the floating control remains
  // only on mobile, where the rail is hidden. The keybinding listener above
  // stays active either way.
  if (!isMobile) {
    return null;
  }

  return (
    <div
      className="pointer-events-none fixed left-[calc(var(--workspace-controls-left)+var(--nav-rail-width,0px))] top-[var(--workspace-controls-top)] z-50 flex h-[var(--workspace-topbar-height)] items-center"
      data-sidebar-control=""
    >
      <Tooltip>
        <TooltipTrigger
          render={
            <SidebarTrigger
              className={cn(
                "pointer-events-auto",
                isSidebarVisible &&
                  stageBackdropVariant &&
                  "[:hover,[data-pressed]]:bg-white/15 focus-visible:ring-white/90 focus-visible:ring-offset-blue-700 [&_svg]:stroke-white/90! [&_svg]:opacity-100! [&_svg]:hover:stroke-white!",
              )}
              aria-label="Toggle main sidebar"
            />
          }
        />
        <TooltipPopup side="bottom">
          Toggle main sidebar{shortcutLabel ? ` (${shortcutLabel})` : ""}
        </TooltipPopup>
      </Tooltip>
    </div>
  );
}

export function AppSidebarLayout({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const pathname = useLocation({ select: (location) => location.pathname });
  const isOnInbox = isInboxPathname(pathname);
  const [inboxSlot, setInboxSlot] = useState<HTMLDivElement | null>(null);
  const sidebarSection = foldsIntoHomeSidebar(pathname) ? "home" : appSidebarSection(pathname);
  // Apps' launcher and directory, apps without an area of their own, and an
  // app's landing (appGate) are one full page: no sidebar beside them.
  const { org: activeOrg } = useRegulationOrg();
  const appAreaView = useAppAreaView(pathname, activeOrg?.id ?? null);
  // Workflows (the list and a workflow's chart) is full width: Home's sidebar
  // beside it (Apps, Recents) has nothing to do with it; the rail leads back.
  const hasSidebar =
    !isOnInbox &&
    !isWorkflowsPathname(pathname) &&
    (sidebarSection !== "apps" ||
      appsSidebarShown(appAreaFromPathname(pathname) !== null, appAreaView));
  // The page's corners change at once when the sidebar comes or goes with it.
  const sidebarSwitching = useSidebarPresenceSwitch(hasSidebar);
  useToolBackShortcut();
  usePendingThreadSpaces();
  const isMacosDesktop = isElectron && isMacPlatform(navigator.platform);
  // The resize clamps reserve the labelled rail's width.
  const navRailWidth = NAV_RAIL_WIDTH;
  // Subscribed rather than read once: the clamp must track live window size,
  // and a clamped drag ends with an unchanged width, which skips the re-render
  // that would otherwise refresh a render-time snapshot.
  const viewportWidth = useSyncExternalStore(subscribeToViewportWidth, readViewportWidth);
  const {
    wrapperRef,
    width: sidebarWidth,
    onResize,
  } = useAppSidebarWidth(sidebarSection, viewportWidth - navRailWidth);
  // The rail is always visible whenever the sidebar is resizable (both render
  // from the md breakpoint), so it comes out of the resizable budget.
  const sidebarMaximumWidth = isOnInbox
    ? resolveInboxSidebarWidth(INBOX_SIDEBAR_MAX_WIDTH, viewportWidth - navRailWidth)
    : resolveThreadSidebarMaximumWidth(viewportWidth - navRailWidth);
  // The root layout fills the viewport. This bound already reserves both the
  // nav rail and main content, so dragging needs no per-frame geometry reads.
  const sidebarResizable = useMemo(
    () => ({
      dragToCollapse: !isOnInbox,
      maxWidth: sidebarMaximumWidth,
      minWidth: isOnInbox ? INBOX_SIDEBAR_MIN_WIDTH : THREAD_SIDEBAR_MIN_WIDTH,
      onResize,
    }),
    [isOnInbox, sidebarMaximumWidth, onResize],
  );
  const [isWindowFullscreen, setIsWindowFullscreen] = useState(() => {
    const getWindowFullscreenState = window.desktopBridge?.getWindowFullscreenState;
    return isMacosDesktop && typeof getWindowFullscreenState === "function"
      ? getWindowFullscreenState()
      : false;
  });
  const sidebarProviderStyle = {
    "--sidebar-width": `${sidebarWidth}px`,
    "--sidebar-left-inset": "var(--nav-rail-width, 0px)",
    ...(isMacosDesktop && !isWindowFullscreen
      ? { "--workspace-controls-left": MACOS_TRAFFIC_LIGHTS_LEFT_INSET }
      : {}),
  } as CSSProperties;

  useEffect(() => {
    if (!isMacosDesktop) return;
    const bridge = window.desktopBridge;
    if (!bridge) return;
    const { getWindowFullscreenState, onWindowFullscreenStateChange } = bridge;
    if (
      typeof getWindowFullscreenState !== "function" ||
      typeof onWindowFullscreenStateChange !== "function"
    ) {
      return;
    }

    const unsubscribe = onWindowFullscreenStateChange(setIsWindowFullscreen);
    setIsWindowFullscreen(getWindowFullscreenState());
    return unsubscribe;
  }, [isMacosDesktop]);

  useEffect(() => {
    const onMenuAction = window.desktopBridge?.onMenuAction;
    if (typeof onMenuAction !== "function") {
      return;
    }

    const unsubscribe = onMenuAction((action) => {
      if (action === "open-settings") {
        const isSettingsRoute = /^\/settings(\/|$)/.test(pathname);
        if (!isSettingsRoute) {
          void navigate({ to: "/settings" });
        }
      }
    });

    return () => {
      unsubscribe?.();
    };
  }, [navigate, pathname]);

  return (
    <SidebarContentSlot value={inboxSlot}>
      <OrgSegmentCanonicalizer />
      <SidebarProvider
        ref={wrapperRef}
        className="h-dvh! min-h-0!"
        defaultOpen
        // One inset, Linear-style: the rail and the search strip sit on the
        // window surface, and the sidebar plus the page share a single rounded
        // card. The geometry lives in index.css under [data-shell-frame].
        data-shell-frame=""
        data-shell-sidebar-switch={sidebarSwitching ? "" : undefined}
        style={sidebarProviderStyle}
      >
        <NavRail topInset={isMacosDesktop && !isWindowFullscreen} standalone={isOnInbox} />
        <ShellTopBar />
        {!isOnInbox && <AppTour />}
        {/* The inbox lays out its own list beside the update, inside the
            page, as Linear does; it has no side panel. */}
        {hasSidebar && (
          <Sidebar
            side="left"
            collapsible="offcanvas"
            data-app-sidebar=""
            className="border-r border-sidebar-border bg-sidebar text-sidebar-foreground"
            resizable={sidebarResizable}
          >
            {sidebarSection === "settings" ? (
              <>
                <SidebarChromeHeader isElectron={isElectron} />
                <SettingsSidebarNav pathname={pathname} />
              </>
            ) : sidebarSection === "home" ? (
              <>
                <SidebarChromeHeader isElectron={isElectron} />
                <HomeSidebar />
              </>
            ) : sidebarSection === "apps" ? (
              <>
                <SidebarChromeHeader isElectron={isElectron} />
                <AppsSidebar />
              </>
            ) : sidebarSection === "governance" ? (
              <>
                <SidebarChromeHeader isElectron={isElectron} />
                <RegulationSidebarNav pathname={pathname} />
                <RegulationSidebarFooter />
              </>
            ) : (
              <ThreadSidebar />
            )}
            {/* Cancel an unfinished drag when navigation changes its destination preference. */}
            <SidebarRail key={sidebarSection} />
          </Sidebar>
        )}
        {/* The inset is the layout's, not the page's: every route renders
            inside this one frame, whatever wrapper it brings. */}
        <div data-shell-page="" className="relative flex min-h-0 min-w-0 flex-1">
          {children}
        </div>
        {hasSidebar && <SidebarControl />}
      </SidebarProvider>
    </SidebarContentSlot>
  );
}
